import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Pencil } from 'lucide-react';

/**
 * Mot du titre écrit au crayon, souligné, puis remplacé par le suivant.
 *
 * Retenue voulue : un seul tour (chaque mot une fois, au moins 4 s par mot),
 * retour au premier mot, puis une longue pause avant un nouveau tour, et
 * seulement si l'onglet est visible. Le survol fige l'animation, mot complet
 * et souligné. Sans animation si le visiteur l'a désactivée (reduced motion).
 */

/** Orange du thème Wanzo (tailwind.config.js, `warning`, bouton « S'inscrire »). */
const INK = '#ee872b';
/** Crayon : même orange, un ton plus sombre pour se détacher du mot. */
const PENCIL = '#c2621a';

const TYPE_MS = 120;        // une lettre
const ERASE_MS = 45;        // une lettre effacée
const UNDERLINE_MS = 650;   // trait de soulignement
const WORD_MIN_MS = 4500;   // durée minimale d'un mot à l'écran (consigne : au moins 4 s)
const REST_MS = 30000;      // pause entre deux tours
const RESUME_AFTER_HOVER_MS = 1500;

type Phase = 'typing' | 'underline' | 'hold' | 'erasing' | 'rest';

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && !!window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function RotatingWord({ words }: { words: string[] }) {
  const reduced = useRef(prefersReducedMotion()).current;
  const [index, setIndex] = useState(0);
  const [length, setLength] = useState(reduced ? words[0].length : 0);
  const [phase, setPhase] = useState<Phase>(reduced ? 'rest' : 'typing');
  const [hovered, setHovered] = useState(false);
  /** Mots déjà passés dans le tour en cours (le tour finit sur le premier mot). */
  const [shownInRound, setShownInRound] = useState(0);
  const wordStartedAt = useRef(Date.now());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const word = words[index] ?? '';

  // Changement de langue : on repart proprement du premier mot.
  useEffect(() => {
    setIndex(0);
    setLength(reduced ? words[0].length : 0);
    setPhase(reduced ? 'rest' : 'typing');
    setShownInRound(0);
    wordStartedAt.current = Date.now();
  }, [words, reduced]);

  useEffect(() => {
    if (reduced || hovered) return;
    const later = (ms: number, fn: () => void) => { timer.current = setTimeout(fn, ms); };

    switch (phase) {
      case 'typing':
        if (length < word.length) later(TYPE_MS, () => setLength((l) => l + 1));
        else later(200, () => setPhase('underline'));
        break;
      case 'underline':
        later(UNDERLINE_MS, () => setPhase('hold'));
        break;
      case 'hold': {
        // Fin du tour : on est revenu au premier mot après avoir montré les autres.
        if (index === 0 && shownInRound > 0) { setPhase('rest'); break; }
        const elapsed = Date.now() - wordStartedAt.current;
        later(Math.max(WORD_MIN_MS - elapsed, 1200), () => setPhase('erasing'));
        break;
      }
      case 'erasing':
        if (length > 0) later(ERASE_MS, () => setLength((l) => l - 1));
        else later(250, () => {
          setIndex((i) => (i + 1) % words.length);
          setShownInRound((n) => n + 1);
          wordStartedAt.current = Date.now();
          setPhase('typing');
        });
        break;
      case 'rest':
        // Nouveau tour seulement si le visiteur est toujours là (onglet visible).
        later(REST_MS, () => {
          // Onglet masqué : on attend son retour (écouteur visibilitychange ci-dessous).
          if (document.visibilityState !== 'visible') return;
          setShownInRound(0);
          wordStartedAt.current = Date.now();
          setPhase('erasing');
        });
        break;
    }
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [phase, length, word, index, shownInRound, hovered, reduced, words.length]);

  // Onglet revenu au premier plan pendant le repos : on relance l'attente avant un nouveau tour.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && phase === 'rest') {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => { setShownInRound(0); wordStartedAt.current = Date.now(); setPhase('erasing'); }, REST_MS);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [phase]);

  const onEnter = () => {
    if (reduced) return;
    if (timer.current) clearTimeout(timer.current);
    setHovered(true);
    // Figé lisible : le mot entier, souligné.
    setLength(word.length);
    if (phase !== 'rest') setPhase('hold');
  };
  const onLeave = () => {
    if (reduced) return;
    // Reprise en douceur : le mot reste encore un instant avant d'être effacé.
    wordStartedAt.current = Date.now() - (WORD_MIN_MS - RESUME_AFTER_HOVER_MS);
    setHovered(false);
  };

  const drawn = phase === 'hold' || phase === 'rest' || phase === 'underline';
  const writing = !reduced && !hovered && (phase === 'typing' || phase === 'underline');

  return (
    <span
      className="relative inline-block whitespace-nowrap"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {/* Les lecteurs d'écran lisent un mot stable, pas la frappe. */}
      <span className="sr-only">{words[0]}</span>
      <span
        aria-hidden="true"
        style={{ color: INK }}
      >
        {word.slice(0, length)}
        {/* Hauteur réservée quand le mot est vide, pour que la ligne ne saute pas. */}
        {length === 0 && <span className="inline-block w-[0.1em]">&#8203;</span>}
      </span>

      {/* Soulignement tracé à la main. */}
      <svg
        aria-hidden="true"
        className="absolute left-0 w-full pointer-events-none"
        style={{ bottom: '-0.12em', height: '0.32em' }}
        viewBox="0 0 100 12"
        preserveAspectRatio="none"
      >
        <motion.path
          d="M2 8 C 22 4, 45 10, 63 6 S 90 5, 98 7"
          fill="none"
          stroke={INK}
          strokeWidth={3}
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          initial={false}
          animate={{ pathLength: drawn && length > 0 ? 1 : 0, opacity: drawn && length > 0 ? 1 : 0 }}
          transition={{ duration: phase === 'underline' ? UNDERLINE_MS / 1000 : 0.2, ease: 'easeInOut' }}
        />
      </svg>

      {/* Le crayon : suit la frappe, puis glisse le long du soulignement. */}
      <motion.span
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{ color: PENCIL }}
        initial={false}
        animate={
          phase === 'underline'
            ? { left: ['0%', '100%'], top: '78%', opacity: writing ? 1 : 0 }
            : { left: '100%', top: '-8%', opacity: writing ? 1 : 0 }
        }
        transition={
          phase === 'underline'
            ? { left: { duration: UNDERLINE_MS / 1000, ease: 'easeInOut' }, top: { duration: 0.15 }, opacity: { duration: 0.2 } }
            : { duration: 0.12 }
        }
      >
        <Pencil className="w-[0.45em] h-[0.45em] -rotate-12" strokeWidth={2.5} />
      </motion.span>
    </span>
  );
}
